import { useCallback, useEffect, useState } from "react";
import {
  displayTitle,
  fetchSaved,
  metaLine,
  unsave,
  type SavedItem,
} from "../lib/savedPage";
import { formatPacificDate } from "../lib/format";
import { CARD_GRID } from "../lib/cardGrid";

/**
 * The starred listings, ported from `server/pages/saved.py`.
 *
 * All the logic lives in `lib/savedPage.ts` and is tested there. What is left
 * here is rendering plus the four states this page owes the user -- loading,
 * error, empty, and the per-card failure of an un-save -- which are the ones
 * most often skipped and each of which looks identical to "broken" when it is
 * missing.
 *
 * This page is the sole writer of the saved-item state; it writes through
 * `repositories/state.py`.
 */

function Card({ item, onUnsave }: { item: SavedItem; onUnsave: (key: string) => void }) {
  const meta = metaLine(item);
  const savedAt = formatPacificDate(item.saved_at);

  return (
    <li className="rounded-md border border-neutral-800 bg-neutral-900 p-3 space-y-2">
      {item.image_url ? (
        // The image is the link to the listing. `rel` is not optional on a
        // target=_blank link to a marketplace: without `noopener` the opened
        // page gets a handle on this one through `window.opener`.
        <a href={item.url || undefined} target="_blank" rel="noopener noreferrer">
          <img
            src={item.image_url}
            alt=""
            loading="lazy"
            className="w-full rounded aspect-square object-cover bg-neutral-800"
          />
        </a>
      ) : null}

      <div>
        <p className="font-semibold text-sm">{item.artist || "Unknown"}</p>
        {/* `capitalize` rather than a JS title-case pass: a naive title-case
            mangles "USA" to "Usa". CSS only changes rendering,
            so the underlying value stays what the scraper recorded. */}
        <p className="text-sm text-neutral-300 capitalize">{displayTitle(item)}</p>
      </div>

      {meta ? <p className="text-xs text-neutral-500">{meta}</p> : null}
      {savedAt ? <p className="text-xs text-neutral-500">Saved {savedAt}</p> : null}

      {item.url ? (
        <a
          href={item.url}
          target="_blank"
          rel="noopener noreferrer"
          className="block text-xs text-neutral-300 underline hover:text-white"
        >
          View listing
        </a>
      ) : null}

      <button
        onClick={() => onUnsave(item.key)}
        className="w-full rounded bg-neutral-100 px-3 py-1.5 text-sm text-neutral-900 hover:bg-white"
      >
        ★ Unsave
      </button>
    </li>
  );
}

export default function Saved() {
  const [items, setItems] = useState<SavedItem[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);

  const load = useCallback(() => {
    setError(null);
    fetchSaved()
      .then(setItems)
      .catch((err: unknown) => setError(err instanceof Error ? err.message : String(err)));
  }, []);

  useEffect(load, [load]);

  const onUnsave = useCallback(
    (key: string) => {
      setActionError(null);
      // Optimistic: the card disappears on click immediately, before the
      // server confirms. On failure the item is put back rather than left
      // missing, so the screen never disagrees with the server about what is
      // saved.
      const previous = items;
      setItems((current) => current?.filter((i) => i.key !== key) ?? null);
      unsave(key).catch((err: unknown) => {
        setItems(previous);
        setActionError(err instanceof Error ? err.message : String(err));
      });
    },
    [items],
  );

  if (error) {
    return (
      <section className="space-y-3">
        <h1 className="text-xl font-semibold">Saved</h1>
        <div className="rounded-md border border-red-900 bg-red-950/40 p-4 text-sm">{error}</div>
        <button
          onClick={load}
          className="rounded-md bg-neutral-100 px-4 py-2 text-sm text-neutral-900 hover:bg-white"
        >
          Try again
        </button>
      </section>
    );
  }

  if (items === null) {
    return (
      <section className="space-y-3">
        <h1 className="text-xl font-semibold">Saved</h1>
        <p className="text-sm text-neutral-400">Loading…</p>
      </section>
    );
  }

  return (
    <section className="space-y-4">
      <div>
        <h1 className="text-xl font-semibold">Saved</h1>
        <p className="text-sm text-neutral-400">
          {items.length === 0
            ? "No saved items yet. Use the star button on listings to save them."
            : `${items.length} saved item${items.length === 1 ? "" : "s"}`}
        </p>
      </div>

      {actionError ? (
        <div className="rounded-md border border-amber-900 bg-amber-950/40 p-3 text-sm">
          {actionError}
        </div>
      ) : null}

      <ul className={CARD_GRID}>
        {items.map((item) => (
          <Card key={item.key} item={item} onUnsave={onUnsave} />
        ))}
      </ul>
    </section>
  );
}
